import { effect, inject, Component, Input, type OnChanges, type SimpleChanges, ViewChild } from '@angular/core';
import { type ChartConfiguration, type ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';

import { ThemeService } from '../../services/theme.service';
import { applyChartThemeDefaults, applyChartThemeToChart, CHART_DATA } from '../../utilities/chart-theme.util';

export interface LineChartDataset {
  label: string;
  data: number[];
  backgroundColor?: string;
  borderColor?: string;
  fill?: boolean;
  tension?: number;
}

@Component({
  selector: 'app-line-chart',
  standalone: false,
  templateUrl: './line-chart.component.html',
  styleUrl: './line-chart.component.scss'
})
export class LineChartComponent implements OnChanges {
  @Input() title: string = 'Line Chart';
  @Input() labels: string[] = [];
  @Input() data: number[] = [];
  @Input() datasets: LineChartDataset[] = [];
  @Input() xAxisLabel: string = '';
  @Input() yAxisLabel: string = '';
  @Input() integerYAxis: boolean = false;
  @Input() yAxisFormatter?: (value: number) => string;

  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  private readonly themeService = inject(ThemeService);

  constructor() {
    // Re-theme chart.js ink (text + gridlines) whenever the light/dark theme changes.
    effect(() => {
      const isDark = this.themeService.isDark();
      applyChartThemeDefaults(isDark);
      applyChartThemeToChart(this.chart?.chart, isDark);
      this.chart?.update();
    });
  }

  public lineChartType: ChartType = 'line';
  public lineChartData: ChartConfiguration<'line'>['data'] = {
    labels: [],
    datasets: [
      {
        data: [],
        label: '',
        backgroundColor: CHART_DATA.primaryAreaFill,
        borderColor: CHART_DATA.primaryBorder,
        borderWidth: 2,
        fill: true,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5
      }
    ]
  };

  public lineChartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        display: true,
        position: 'top'
      },
      title: { display: false }
    },
    scales: {
      x: {
        title: { display: true, text: '' }
      },
      y: {
        beginAtZero: false,
        title: { display: true, text: '' }
      }
    }
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['labels'] || changes['data'] || changes['datasets']) {
      this.lineChartData.labels = [...this.labels];

      if (this.datasets && this.datasets.length > 0) {
        this.lineChartData.datasets = this.datasets.map(ds => ({
          label: ds.label,
          data: [...ds.data],
          backgroundColor: ds.backgroundColor || CHART_DATA.primaryAreaFill,
          borderColor: ds.borderColor || CHART_DATA.primaryBorder,
          borderWidth: 2,
          fill: ds.fill !== undefined ? ds.fill : true,
          tension: ds.tension !== undefined ? ds.tension : 0.3,
          pointRadius: 3,
          pointHoverRadius: 5
        }));
        if (this.lineChartOptions?.plugins?.legend) {
          this.lineChartOptions.plugins.legend.display = this.datasets.length > 1;
        }
      } else {
        this.lineChartData.datasets = [
          {
            label: this.title,
            data: [...this.data],
            backgroundColor: CHART_DATA.primaryAreaFill,
            borderColor: CHART_DATA.primaryBorder,
            borderWidth: 2,
            fill: true,
            tension: 0.3,
            pointRadius: 3,
            pointHoverRadius: 5
          }
        ];
        if (this.lineChartOptions?.plugins?.legend) {
          this.lineChartOptions.plugins.legend.display = false;
        }
      }

      this.chart?.update();
    }

    if (changes['xAxisLabel'] || changes['yAxisLabel'] || changes['integerYAxis'] || changes['yAxisFormatter']) {
      if (this.lineChartOptions?.scales) {
        const xScale = this.lineChartOptions.scales['x'];
        const yScale = this.lineChartOptions.scales['y'];
        if (xScale?.title) {
          xScale.title.text = this.xAxisLabel;
        }
        if (yScale) {
          if (yScale.title) {
            yScale.title.text = this.yAxisLabel;
          }
          if (this.yAxisFormatter) {
            const formatter = this.yAxisFormatter;
            yScale.ticks = {
              callback: function (tickValue: string | number) {
                if (typeof tickValue === 'number') {
                  return formatter(tickValue);
                }
                return tickValue;
              }
            };
          } else if (this.integerYAxis) {
            yScale.ticks = {
              callback: function (tickValue: string | number) {
                if (typeof tickValue === 'number' && Number.isInteger(tickValue)) {
                  return tickValue;
                }
                return null;
              }
            };
          } else {
            yScale.ticks = {};
          }
        }
      }
      this.chart?.update();
    }
  }
}
